
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Users, CheckCircle, XCircle, Clock, Trash2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface Registration {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  course: string;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
}

interface RegistrationsSectionProps {
  registrations: Registration[];
  updateRegistrationStatus: (id: string, status: 'pending' | 'approved' | 'rejected') => Promise<boolean>;
  deleteRegistration: (id: string) => Promise<boolean>;
}

export const RegistrationsSection = ({ 
  registrations, 
  updateRegistrationStatus, 
  deleteRegistration 
}: RegistrationsSectionProps) => {
  const getStatusBadge = (status: string) => {
    const statusConfig = {
      pending: { label: 'Ուսումնասիրվում է', className: 'bg-yellow-500/20 text-yellow-300 border-yellow-400/30', icon: Clock },
      approved: { label: 'Ընդունված', className: 'bg-primary/10 text-primary border-border/70', icon: CheckCircle },
      rejected: { label: 'Մերժված', className: 'bg-red-500/20 text-red-300 border-red-400/30', icon: XCircle }
    };
    
    const config = statusConfig[status as keyof typeof statusConfig];
    const Icon = config.icon;
    
    return (
      <Badge className={`${config.className}`}>
        <Icon className="h-3 w-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('hy-AM', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleDeleteRegistration = async (registrationId: string) => {
    await deleteRegistration(registrationId);
  };

  return (
    <Card className="border-border/70 bg-card/80">
      <CardHeader className="px-4 sm:px-6">
        <CardTitle className="text-muted-foreground flex items-center gap-2">
          <Users className="h-5 w-5" />
          Ուղարկված հայտեր ({registrations.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4 sm:px-6">
        {registrations.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            Հայտեր դեռ չկան
          </div>
        ) : (
          <div className="-mx-4 overflow-x-auto px-4 [scrollbar-width:thin] sm:mx-0 sm:px-0">
            <Table className="min-w-[860px]">
              <TableHeader>
                <TableRow className="border-border/70">
                  <TableHead className="text-muted-foreground">Անուն Ազգանուն</TableHead>
                  <TableHead className="text-muted-foreground">Էլ. փոստ</TableHead>
                  <TableHead className="text-muted-foreground">Հեռախոս</TableHead>
                  <TableHead className="text-muted-foreground">Դասընթաց</TableHead>
                  <TableHead className="text-muted-foreground">Կարգավիճակ</TableHead>
                  <TableHead className="text-muted-foreground">Ամսաթիվ</TableHead>
                  <TableHead className="text-muted-foreground">Գործողություններ</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {registrations.map((registration) => (
                  <TableRow key={registration.id} className="border-border/70">
                    <TableCell className="text-muted-foreground">
                      {registration.first_name} {registration.last_name}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {registration.email}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {registration.phone}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {registration.course}
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(registration.status)}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {formatDate(registration.created_at)}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {registration.status !== 'approved' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => updateRegistrationStatus(registration.id, 'approved')}
                            className="text-primary border-border/70 hover:bg-primary/10 text-xs"
                          >
                            Ընդունել
                          </Button>
                        )}
                        {registration.status !== 'rejected' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => updateRegistrationStatus(registration.id, 'rejected')}
                            className="text-red-400 border-red-400/30 hover:bg-red-400/20 text-xs"
                          >
                            Մերժել
                          </Button>
                        )}
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-400 border-red-400/30 hover:bg-red-400/20 text-xs"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="bg-card border-red-400/30">
                            <AlertDialogHeader>
                              <AlertDialogTitle className="text-red-300">
                                Հաստատել ջնջումը
                              </AlertDialogTitle>
                              <AlertDialogDescription className="text-red-200">
                                Դուք վստա՞հ եք, որ ցանկանում եք ջնջել {registration.first_name} {registration.last_name}-ի հայտը։ 
                                Այս գործողությունը հետարկել հնարավոր չէ։
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel className="border-border text-muted-foreground hover:bg-muted/50">
                                Չեղարկել
                              </AlertDialogCancel>
                              <AlertDialogAction 
                                onClick={() => handleDeleteRegistration(registration.id)}
                                className="bg-red-600 hover:bg-red-700 text-primary-foreground"
                              >
                                Ջնջել
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
